import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  Search,
  ChevronRight,
  MessageSquare,
  CheckCircle,
  XCircle,
  Loader2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import html2pdf from "html2pdf.js";
import { marked } from "marked";

const MeetingRecords = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [meetings, setMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const itemsPerPage = 10; // 서버에도 동일하게 적용

  const navigate = useNavigate();
  const { api } = useAuth();

  // 서버에서 해당 페이지의 회의 기록을 가져옴
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        setIsLoading(true);
        setFetchError("");
        const resp = await api.get(`/meeting/records?p=${currentPage}`);
        // APIResponse 형태: { data: [ {id, title, date, time, duration, status, summary, detailReport?}, ... ] }
        const data = resp.data.data || [];
        setMeetings(data);
      } catch (e) {
        console.error("회의 기록 가져오기 실패:", e);
        setFetchError("회의 기록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeetings();
  }, [api, currentPage]);

  function formatCreatedAt(createdAt) {
    console.log(createdAt);
    // createdAt: "2025-06-04 08:45:45"
    const [datePart, timePart] = createdAt.split(" ");
    if (!datePart || !timePart) return ""; // 형식이 다르면 빈 문자열 반환

    const [year, month, day] = datePart.split("-").map((s) => parseInt(s, 10));
    const [hour, minute, second] = timePart
      .split(":")
      .map((s) => parseInt(s, 10));

    // month - 1 을 해 주어야 JS Date가 올바르게 인식합니다.
    const dateObj = new Date(year, month - 1, day, hour, minute, second);

    // locale 옵션을 통해 한국어로 포맷
    return dateObj.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  const downloadPDF = async () => {
    // 마크다운을 HTML로 변환
    const htmlContent = marked(selectedMeeting.detail_report || "");

    const element = document.createElement("div");
    element.innerHTML = `
    <div style="padding: 40px; font-family: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;">
      <h1 style="color: #0d9488; font-size: 28px; margin-bottom: 10px;">
        ${selectedMeeting.title}
      </h1>
      
      <div style="border-top: 2px solid #0d9488; padding-top: 20px; margin-bottom: 30px;">
        <table style="width: 100%; font-size: 14px;">
          <tr>
            <td style="padding: 5px 0;"><strong>회의 ID:</strong></td>
            <td>${selectedMeeting.id}</td>
          </tr>
          <tr>
            <td style="padding: 5px 0;"><strong>날짜:</strong></td>
            <td>${formatCreatedAt(selectedMeeting.created_at)}</td>
          </tr>
          <tr>
            <td style="padding: 5px 0;"><strong>시간:</strong></td>
            <td>${selectedMeeting.time}</td>
          </tr>
          <tr>
            <td style="padding: 5px 0;"><strong>소요시간:</strong></td>
            <td>${selectedMeeting.duration}</td>
          </tr>
        </table>
      </div>
      
      <div style="line-height: 1.8; color: #374151;">
        ${htmlContent}
      </div>
      
      <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px;">
        <p>이 문서는 Elva AI Meeting Summary에서 자동 생성되었습니다.</p>
        <p>생성일시: ${new Date().toLocaleString("ko-KR")}</p>
      </div>
    </div>
  `;

    const opt = {
      margin: 10,
      filename: `회의록_${selectedMeeting.title.replace(
        /[^a-zA-Z0-9가-힣]/g,
        "_"
      )}_${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    try {
      await html2pdf().from(element).set(opt).save();
    } catch (error) {
      console.error("PDF 다운로드 실패:", error);
      alert("PDF 다운로드 중 오류가 발생했습니다.");
    }
  };
  // 검색 및 필터 적용 (클라이언트 사이드)
  const filteredMeetings = meetings.filter((meeting) => {
    const matchesSearch =
      meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.summary.toLowerCase().includes(searchTerm.toLowerCase());

    if (selectedFilter === "all") return matchesSearch;

    const meetingDate = new Date(meeting.date);
    const today = new Date();

    if (selectedFilter === "thisWeek") {
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      return matchesSearch && meetingDate >= weekAgo;
    }
    if (selectedFilter === "thisMonth") {
      return (
        matchesSearch &&
        meetingDate.getMonth() === today.getMonth() &&
        meetingDate.getFullYear() === today.getFullYear()
      );
    }
    return matchesSearch;
  });

  // 검색어나 필터 변경 시 반드시 첫 페이지로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter]);

  const formatDate = (dateStr) => {
    // 1) "YYYY-MM-DD HH:mm:ss" 등 어떤 형식이 와도 제대로 파싱하려면
    //    new Date(...)만 쓰지 말고 연·월·일·시·분·초를 직접 분리해서 Date 객체
    const [datePart, timePart] = dateStr.split(" ");
    const [year, month, day] = datePart.split("-").map((v) => parseInt(v, 10));
    const [hour = 0, minute = 0, second = 0] = timePart
      ? timePart.split(":").map((v) => parseInt(v, 10))
      : [];
    // JS Date의 month는 0-11이므로 month-1
    const date = new Date(year, month - 1, day, hour, minute, second);

    const today = new Date();
    // 로컬 기준 "오늘"인지 비교하려면 getFullYear/getMonth/getDate 사용
    if (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    ) {
      return "오늘";
    }

    // "어제"인지 판별: 오늘 날짜에서 하루 뺀 날짜를 만들어서 비교
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()
    ) {
      return "어제";
    }
    marked.setOptions({
      breaks: true, // 줄바꿈을 <br>로 변환
      gfm: true, // GitHub Flavored Markdown 지원
      tables: true, // 테이블 지원
      sanitize: false, // HTML 태그 허용
      smartLists: true, // 스마트 리스트
      smartypants: true, // 스마트 인용부호
    });

    // 7일 이내인지 계산하려면, 오늘과 date 사이 일수 차이를 구합니다
    const millisPerDay = 1000 * 60 * 60 * 24;
    // 두 날짜를 자정(00:00) 기준으로 계산하기 위해, 시간 정보를 제거
    const utc1 = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const utc2 = Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const diffDays = Math.floor((utc2 - utc1) / millisPerDay);

    if (diffDays < 7) {
      return `${diffDays}일 전`;
    }

    // 그 외는 연·월·일 형식으로
    return date.toLocaleDateString("ko-KR", {
      month: "long",
      day: "numeric",
      year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  };
  // 이전/다음 페이지 버튼 활성 여부
  const hasPrevPage = currentPage > 1;
  const hasNextPage = meetings.length === itemsPerPage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">돌아가기</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-2xl font-bold text-gray-900">회의 기록</h1>
            </div>
            <div className="text-sm text-gray-500">
              총 {meetings.length}개 결과 (서버 페이지 {currentPage})
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="회의 제목 또는 내용으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedFilter("all")}
              className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                selectedFilter === "all"
                  ? "bg-teal-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              전체
            </button>
            <button
              onClick={() => setSelectedFilter("thisWeek")}
              className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                selectedFilter === "thisWeek"
                  ? "bg-teal-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              이번 주
            </button>
            <button
              onClick={() => setSelectedFilter("thisMonth")}
              className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                selectedFilter === "thisMonth"
                  ? "bg-teal-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              이번 달
            </button>
          </div>
        </div>

        {/* 로딩 또는 에러 표시 */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
          </div>
        )}
        {fetchError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center text-red-700">
            {fetchError}
          </div>
        )}

        {/* Meeting List */}
        {!isLoading && !fetchError && filteredMeetings.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">검색 결과가 없습니다</p>
          </div>
        ) : (
          !isLoading &&
          filteredMeetings.map((meeting) => (
            <div
              key={meeting.id}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 mb-4 cursor-pointer group"
              onClick={() => {
                if (meeting.status === "completed") {
                  setSelectedMeeting(meeting);
                  setIsModalOpen(true);
                }
              }}
              style={{
                cursor: meeting.status === "completed" ? "pointer" : "default",
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                        {meeting.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(meeting.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {meeting.time}
                        </span>
                        <span className="flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-1 rounded-lg font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          {meeting.duration}
                        </span>
                        {meeting.status === "completed" && (
                          <span className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-lg font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            요약 완료
                          </span>
                        )}
                        {meeting.status === "analyzing" && (
                          <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-lg font-medium">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            분석 중
                          </span>
                        )}
                        {meeting.status === "failed" && (
                          <span className="flex items-center gap-1 bg-red-50 text-red-700 px-2 py-1 rounded-lg font-medium">
                            <XCircle className="w-3.5 h-3.5" />
                            실패
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {meeting.summary}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="font-mono">{meeting.id}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-500 transition-colors ml-4" />
              </div>
            </div>
          ))
        )}

        {/* Pagination Controls */}
        {!isLoading && !fetchError && meetings.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={!hasPrevPage}
              className={`p-2 rounded-lg transition-colors ${
                !hasPrevPage
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>

            <span className="text-gray-700 font-medium">{currentPage}</span>

            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={!hasNextPage}
              className={`p-2 rounded-lg transition-colors ${
                !hasNextPage
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {isModalOpen && selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-500 to-blue-600 text-white p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">
                    {selectedMeeting.title}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-teal-100">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatCreatedAt(selectedMeeting.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedMeeting.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedMeeting.duration}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedMeeting(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    회의 ID
                  </h3>
                  <p className="font-mono text-sm text-gray-600">
                    {selectedMeeting.id}
                  </p>
                </div>
                {selectedMeeting.detail_report && (
                  <div
                    className="whitespace-pre-line text-gray-700 leading-relaxed prose prose-sm max-w-none
    prose-headings:text-gray-800 
    prose-h1:text-2xl prose-h1:border-b prose-h1:pb-2
    prose-h2:text-xl prose-h2:mt-4
    prose-strong:text-gray-900
    prose-ul:list-disc prose-ul:pl-6
    prose-ol:list-decimal prose-ol:pl-6
    prose-blockquote:border-l-4 prose-blockquote:border-teal-500 prose-blockquote:pl-4
    prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded"
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(selectedMeeting.detail_report),
                    }}
                  />
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedMeeting(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  닫기
                </button>
                <button
                  onClick={downloadPDF}
                  className="px-6 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg hover:shadow-md transition-all"
                >
                  보고서 다운로드
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingRecords;
